// src/hooks/useRecognitionsAPI.ts
// Vite + React hook for fetching/normalizing the recognitions manifest from
// the Supabase Edge Function. Provides an "All" tab and safe item shapes.

import { useEffect, useMemo, useRef, useState } from "react";

// ---------- Types (external shape exposed to components) ----------
export type RecognitionLink = { label: string; href: string };

export type RecognitionItem = {
  id: string;                 // e.g. "intl/3-IntComp"
  group: string;              // "intl" | "press" | "tv" | "collab" | ""
  slug: string;
  title: string;              // always string ("" if missing)
  description: string;        // always string ("" if missing)
  imageUrl?: string;          // undefined if missing
  links: RecognitionLink[];   // always an array (possibly empty)
  // passthrough for debugging:
  paths?: {
    title?: string;
    description?: string;
    image?: string;
    link1?: string;
    link2?: string;
  };
};

export type RecognitionsManifest = {
  updatedAt: string;
  items: RecognitionItem[];
};

export type CategoryDef = { key: string; label: string; count: number };

// ---------- Env / URL handling ----------
const MANIFEST_URL_ENV = (import.meta as any).env?.VITE_RECOGNITIONS_MANIFEST_URL as
  | string
  | undefined;

function resolveManifestUrl(): string {
  const url = MANIFEST_URL_ENV?.trim();
  if (!url) {
    throw new Error(
      "VITE_RECOGNITIONS_MANIFEST_URL is not set. " +
        "Set it to your Supabase function, e.g. https://<project>.functions.supabase.co/awards-manifest"
    );
  }
  return url;
}

// ---------- Fetcher ----------
async function fetchManifestNoCache(baseUrl: string, timeoutMs = 15000): Promise<any> {
  const url = `${baseUrl}?ts=${Date.now()}`;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { cache: "no-store", signal: ctrl.signal } as RequestInit);
    if (!res.ok) throw new Error(`Manifest fetch failed: ${res.status} ${res.statusText}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

// ---------- Normalization ----------
function parseOneLink(raw?: string | null): RecognitionLink | null {
  if (!raw) return null;
  // Typical content: "Label\nhttps://example.com"
  const parts = String(raw).split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) {
    const href = parts[0];
    if (!/^https?:\/\//i.test(href)) return null;
    return { label: "Link", href };
  }
  const [label, href] = parts;
  if (!href || !/^https?:\/\//i.test(href)) return null;
  return { label: label || "Link", href };
}

function normalizeItem(raw: any): RecognitionItem {
  const links: RecognitionLink[] = [];
  const l1 = parseOneLink(raw?.link1);
  const l2 = parseOneLink(raw?.link2);
  if (l1) links.push(l1);
  if (l2) links.push(l2);

  const img = typeof raw?.imageUrl === "string" && raw.imageUrl.trim() ? raw.imageUrl : undefined;

  return {
    id: String(raw?.id ?? ""),
    group: String(raw?.group ?? ""),
    slug: String(raw?.slug ?? ""),
    title: String(raw?.title ?? ""),
    description: String(raw?.description ?? ""),
    imageUrl: img,
    links,
    paths: raw?.paths ?? undefined,
  };
}

function normalizeManifest(json: any): RecognitionsManifest {
  const updatedAt = typeof json?.updatedAt === "string" ? json.updatedAt : new Date().toISOString();
  const rawItems = Array.isArray(json?.items) ? json.items : [];
  const items = rawItems.map(normalizeItem);
  return { updatedAt, items };
}

// ---------- Grouping / categories ----------
const FRIENDLY: Record<string, string> = {
  intl: "International Competitions",
  press: "Press",
  tv: "TV Appearances",
  collab: "Collaborations",
  "": "Recognitions",
};

function labelOf(group: string) {
  return (FRIENDLY[group] ?? group) || "Recognitions";
}

function groupBy(items: RecognitionItem[]) {
  const out: Record<string, RecognitionItem[]> = {};
  for (const it of items) {
    const g = (it.group ?? "").toString();
    (out[g] ??= []).push(it);
  }
  return out;
}

// ---------- Debug surface ----------
declare global {
  interface Window {
    __RECOG_DEBUG?: {
      url: () => string;
      fetch: () => Promise<RecognitionsManifest>;
      sample: (n?: number) => RecognitionItem[];
      setCat: (k: string) => void;
      items: () => RecognitionItem[];
    };
  }
}

// ---------- Hook ----------
export function useRecognitionsAPI() {
  const [manifest, setManifest] = useState<RecognitionsManifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // include an explicit "All" tab; default to "all"
  const [activeCategoryKey, setActiveCategoryKey] = useState<string>("all");

  const manifestUrlRef = useRef<string>("");
  if (!manifestUrlRef.current) manifestUrlRef.current = resolveManifestUrl();

  // Fetch once
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchManifestNoCache(manifestUrlRef.current)
      .then((json) => {
        if (!mounted) return;
        setManifest(normalizeManifest(json));
        setError(null);
      })
      .catch((e: any) => {
        console.error("[recognitions] fetch error", e);
        if (!mounted) return;
        setError(e?.message ?? String(e));
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const allItems = manifest?.items ?? [];

  const groups = useMemo(() => groupBy(allItems), [allItems]);

  const realCategories: CategoryDef[] = useMemo(
    () => {
      // Define custom order: TV, Collaborations, Press, Int'l Competitions
      const order = ["tv", "collab", "press", "intl"];
      const categoriesMap = Object.keys(groups)
        .map((g) => ({ key: g, label: labelOf(g), count: groups[g]?.length ?? 0 }))
        .filter((c) => c.count > 0);
      
      // Sort by custom order
      return categoriesMap.sort((a, b) => {
        const aIdx = order.indexOf(a.key);
        const bIdx = order.indexOf(b.key);
        if (aIdx === -1 && bIdx === -1) return 0;
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      });
    },
    [groups]
  );

  const categoriesWithAll: CategoryDef[] = useMemo(
    () => [{ key: "all", label: "All", count: allItems.length }, ...realCategories],
    [realCategories, allItems.length]
  );

  // Ensure active stays valid
  useEffect(() => {
    if (activeCategoryKey !== "all") {
      const exists = realCategories.some((c) => c.key === activeCategoryKey);
      if (!exists) setActiveCategoryKey("all");
    }
  }, [activeCategoryKey, realCategories]);

  // Items for current tab
  const itemsForActive = useMemo(() => {
    if (activeCategoryKey === "all") return allItems;
    return groups[activeCategoryKey] ?? [];
  }, [activeCategoryKey, groups, allItems]);

  // Debug helpers
  useEffect(() => {
    const url = manifestUrlRef.current;
    window.__RECOG_DEBUG = {
      url: () => url,
      fetch: async () => {
        const json = await fetchManifestNoCache(url);
        return normalizeManifest(json);
      },
      sample: (n = 3) => allItems.slice(0, n),
      setCat: (k: string) => setActiveCategoryKey(k || "all"),
      items: () => allItems,
    };
  }, [allItems]);

  // Finder
  function findById(id: string) {
    return allItems.find((x) => x?.id === id);
  }

  return {
    // data
    manifest,
    allItems,
    items: itemsForActive,

    // meta
    loading,
    error,
    updatedAt: manifest?.updatedAt ?? null,

    // categories
    categories: categoriesWithAll,      // includes {key:"all", ...}
    activeCategoryKey,
    setActiveCategoryKey,

    // env/debug
    manifestUrl: manifestUrlRef.current,

    // helpers
    findById,
    labelOf,
  };
}

export default useRecognitionsAPI;
